/**
 * Dashboard status dropdown options.
 * `value` is what we store in DB/Sheets; `label` is what the UI shows.
 */

/** Artist mapping (IG/TT Status, Snoop Artist Status). */
export const ARTIST_STATUS_OPTIONS = [
  { value: 'Archive', label: 'Archive' },
  { value: 'Priority', label: 'Priority' },
  { value: 'Destroy', label: 'Destroy' },
  { value: 'Junk', label: 'Junk' },
  { value: 'Roster', label: 'Roster' },
  { value: 'Distro', label: 'Distro' },
  { value: 'CM', label: 'Chartmetric' },
]

/** Destroy/Junk — omitted from default mapping views; Strong-ID dup groups drop when all members are these. */
export const ARTIST_IGNORED_STATUSES = new Set(['Destroy', 'Junk'])

export function isArtistIgnoredStatus(status) {
  return ARTIST_IGNORED_STATUSES.has(String(status ?? '').trim())
}

/**
 * Strong-ID duplicates view: when the edited row becomes Destroy/Junk, drop the whole
 * Dup Group iff every peer is (or will be) Destroy/Junk. Mixed groups keep Destroy peers.
 */
export function strongIdDupGroupRowsToRemove(nextStatus, { row, rows } = {}) {
  if (!isArtistIgnoredStatus(nextStatus)) return () => false
  const group = String(row?.['Dup Group'] ?? '').trim()
  if (!group || !Array.isArray(rows) || !rows.length) return () => false

  const editedId = String(row?.id ?? '')
    .trim()
    .replace(/\.0$/, '')
  const groupRows = rows.filter((r) => String(r?.['Dup Group'] ?? '').trim() === group)
  if (!groupRows.length) return () => false

  const allIgnored = groupRows.every((r) => {
    const id = String(r?.id ?? '')
      .trim()
      .replace(/\.0$/, '')
    if (editedId && id === editedId) return true
    return isArtistIgnoredStatus(r?.Status)
  })
  if (!allIgnored) return () => false
  return (r) => String(r?.['Dup Group'] ?? '').trim() === group
}

/** Default IG/TT unsigned table order: Priority → Archive → Chartmetric → other.
 * Roster/Distro ranks support combined roster+distro platform tables (Roster first).
 */
export const UNSIGNED_STATUS_SORT_RANK = {
  Priority: 0,
  Archive: 1,
  CM: 2,
  Roster: 10,
  Distro: 11,
}

/** Snoop mapping Status column. */
export const SNOOP_STATUS_OPTIONS = [
  { value: 'No Status', label: 'IGNORE/INDUSTRY' },
  { value: 'CORE', label: 'SNOOP/TRACK' },
  { value: 'Signed', label: 'SIGNED' },
  { value: 'A&R', label: 'A&R' },
  { value: 'Junk', label: 'Junk' },
  { value: 'Private', label: 'Private' },
  { value: 'Burner', label: 'Burner' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Digital/Marketing', label: 'Digital/Marketing' },
  { value: 'Exec', label: 'Exec' },
  { value: 'Producer', label: 'Producer' },
  { value: 'blog', label: 'blog' },
]

/** Snoop summary dash Status — Signed hidden (use Artist Status Destroy/Signed instead). */
export const SNOOP_DASH_STATUS_OPTIONS = SNOOP_STATUS_OPTIONS.filter(
  (opt) => opt.value !== 'Signed',
)

/** Snoop summary dash Artist Status — Destroy labeled Destroy/Signed. */
export const SNOOP_DASH_ARTIST_STATUS_OPTIONS = ARTIST_STATUS_OPTIONS.map((opt) =>
  opt.value === 'Destroy' ? { ...opt, label: 'Destroy/Signed' } : opt,
)

/**
 * Artist Status values that drop a row from the Snoop summary dashboard.
 * Must mirror zPostgres.SNOOP_DASHBOARD_EXCLUDED_ARTIST_STATUSES.
 */
export const SNOOP_DASHBOARD_EXCLUDED_ARTIST_STATUSES = new Set([
  'Destroy',
  'Junk',
  'Roster',
  'Distro',
])

/** True when snoop Status is blank / unset (dashboard keeps only these). */
export function isBlankSnoopStatus(status) {
  const s = String(status ?? '').trim()
  return !s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none'
}

/** Drop from Snoop dash after a successful Status / Artist Status save. */
export function shouldRemoveSnoopSummaryRow(column, status) {
  if (column === 'Status') return !isBlankSnoopStatus(status)
  if (column === 'Artist Status') {
    return SNOOP_DASHBOARD_EXCLUDED_ARTIST_STATUSES.has(String(status ?? '').trim())
  }
  return false
}

/* -------------------------------------------------------------------------
 * Snoop dash unified status
 *
 * Snoop Status and Artist Status are mutually exclusive by policy: a snoop
 * status means "not an artist". The dash therefore edits one control whose
 * options are namespaced `snoop:<value>` / `artist:<value>` so the two
 * `Junk` values stay distinct, and routes each save to the matching table.
 * ---------------------------------------------------------------------- */

/** Assignable options for the merged Snoop dash Status control. */
export const SNOOP_UNIFIED_STATUS_OPTIONS = [
  { value: 'snoop:No Status', label: 'Snoop Ignore (Not an Artist)' },
  { value: 'snoop:CORE', label: 'Snoop Track' },
  { value: 'snoop:Private', label: 'Snoop Private' },
  { value: 'artist:Archive', label: 'Archive Artist' },
  { value: 'artist:Priority', label: 'Priority Artist' },
  { value: 'artist:Destroy', label: 'Destroy/Signed Artist' },
  { value: 'artist:Junk', label: 'Junk Artist' },
  { value: 'artist:Roster', label: 'Roster Artist' },
  { value: 'artist:Distro', label: 'Distro Artist' },
]

/**
 * Values the merged control renders but never lets you assign. `CM` is set by the
 * Chartmetric import; the legacy snoop taxonomy stays editable on /mapping/snoop.
 */
export const SNOOP_UNIFIED_DISPLAY_ONLY_OPTIONS = [
  { value: 'artist:CM', label: 'Chartmetric Artist', disabled: true },
  { value: 'snoop:Signed', label: 'Signed (snoop)', disabled: true },
  { value: 'snoop:Junk', label: 'Junk (snoop)', disabled: true },
  { value: 'snoop:A&R', label: 'A&R (snoop)', disabled: true },
  { value: 'snoop:Burner', label: 'Burner (snoop)', disabled: true },
  { value: 'snoop:Manager', label: 'Manager (snoop)', disabled: true },
  { value: 'snoop:Digital/Marketing', label: 'Digital/Marketing (snoop)', disabled: true },
  { value: 'snoop:Exec', label: 'Exec (snoop)', disabled: true },
  { value: 'snoop:Producer', label: 'Producer (snoop)', disabled: true },
  { value: 'snoop:blog', label: 'blog (snoop)', disabled: true },
]

export const SNOOP_UNIFIED_ALL_OPTIONS = [
  ...SNOOP_UNIFIED_STATUS_OPTIONS,
  ...SNOOP_UNIFIED_DISPLAY_ONLY_OPTIONS,
]

/** Split `artist:Roster` into `{ target: 'artist', value: 'Roster' }`. */
export function parseSnoopUnifiedStatus(unified) {
  const s = String(unified ?? '').trim()
  if (!s) return { target: null, value: '' }
  const idx = s.indexOf(':')
  if (idx < 0) return { target: null, value: s }
  const target = s.slice(0, idx)
  const value = s.slice(idx + 1)
  if (target !== 'snoop' && target !== 'artist') return { target: null, value: s }
  return { target, value }
}

/**
 * Merged value for a Snoop summary row. Artist Status wins when present;
 * snoop Status is the fallback (only reachable outside the dash membership filter,
 * which already requires a blank snoop Status).
 */
export function snoopUnifiedValue(row) {
  const artist = String(row?.['Artist Status'] ?? '').trim()
  if (artist && !isBlankSnoopStatus(artist)) return `artist:${artist}`
  const snoop = String(row?.Status ?? '').trim()
  if (snoop && !isBlankSnoopStatus(snoop)) return `snoop:${snoop}`
  return ''
}

/** Row patch to paint optimistically — writes only the column that actually changed. */
export function snoopUnifiedOptimisticPatch(unified) {
  const { target, value } = parseSnoopUnifiedStatus(unified)
  if (target === 'snoop') return { Status: value || null, 'Artist Status': null }
  if (target === 'artist') return { 'Artist Status': value || null, Status: null }
  return {}
}

/** Drop from the Snoop dash after a successful merged-status save. */
export function shouldRemoveSnoopUnifiedRow(unified) {
  const { target, value } = parseSnoopUnifiedStatus(unified)
  if (target === 'snoop') return !isBlankSnoopStatus(value)
  if (target === 'artist') return SNOOP_DASHBOARD_EXCLUDED_ARTIST_STATUSES.has(value)
  return false
}

/** Sound Mapping V1 Status (must match zPostgres.SOUND_MAPPING_STATUS_OPTIONS). */
export const SOUND_STATUS_OPTIONS = [
  { value: 'Priority', label: 'Priority' },
  { value: 'Archive', label: 'Archive' },
  { value: 'Search', label: 'Search' },
  { value: 'Roster', label: 'Roster' },
  { value: 'Distro', label: 'Distro' },
  { value: 'Destroy', label: 'Destroy' },
]

/** Luminate Status Tracking — discovery UI options (status + entity for routing).
 * Stored value is always bare (`Signed` / `Unsigned` / `Junk` / `Likely Junk`);
 * entity_type / sheet tab comes from the option suffix (Label|Artist|Track).
 */
export const LUMINATE_STATUS_OPTIONS = [
  { value: 'Signed Label', label: 'Signed Label' },
  { value: 'Signed Artist', label: 'Signed Artist' },
  { value: 'Signed Track', label: 'Signed Track' },
  { value: 'Unsigned Label', label: 'Unsigned Label' },
  { value: 'Unsigned Artist', label: 'Unsigned Artist' },
  { value: 'Unsigned Track', label: 'Unsigned Track' },
  { value: 'Junk Label', label: 'Junk Label' },
  { value: 'Junk Artist', label: 'Junk Artist' },
  { value: 'Junk Track', label: 'Junk Track' },
  { value: 'Likely Junk Label', label: 'Likely Junk Label' },
  { value: 'Likely Junk Artist', label: 'Likely Junk Artist' },
  { value: 'Likely Junk Track', label: 'Likely Junk Track' },
]

/** Mapping UI — bare statuses; entity chosen via Entity Type. */
export const LUMINATE_BARE_STATUS_OPTIONS = [
  { value: 'Signed', label: 'Signed' },
  { value: 'Unsigned', label: 'Unsigned' },
  { value: 'Junk', label: 'Junk' },
  { value: 'Likely Junk', label: 'Likely Junk' },
]

/** Matches zLuminate discovery exclusion (polarity + legacy aliases). */
export const LUMINATE_EXCLUDED_STATUSES = new Set([
  'Signed',
  'Junk',
  'Likely Junk',
  'Signed Label',
  'Junk Label',
  'Likely Junk Label',
  'Signed Artist',
  'Signed Track',
  'Junk Artist',
  'Junk Track',
  'Likely Junk Artist',
  'Likely Junk Track',
])

/**
 * Map a dashboard Luminate option to the Status string stored on sheet/DB.
 * Always bare: Signed|Unsigned|Junk|Likely Junk (entity is separate).
 */
export function luminateStoredStatus(option) {
  const s = String(option ?? '').trim()
  if (!s) return ''
  const signed = /^(Signed|Unsigned)(?:\s+(Label|Artist|Track))?$/i.exec(s)
  if (signed) {
    return signed[1][0].toUpperCase() + signed[1].slice(1).toLowerCase()
  }
  const junk = /^(Junk|Likely Junk)(?:\s+(Label|Artist|Track))?$/i.exec(s)
  if (junk) {
    return junk[1].toLowerCase().startsWith('likely') ? 'Likely Junk' : 'Junk'
  }
  return s
}

/**
 * Discovery polarity bucket: Signed (incl. Junk), Unsigned, or null.
 * Junk* aliases count as Signed so they drop from discovery tables.
 */
export function luminateStatusPolarity(status) {
  const s = String(status ?? '').trim()
  if (!s) return null
  const m = /^(Signed|Unsigned)(?:\s+(Label|Artist|Track))?$/i.exec(s)
  if (m) return m[1][0].toUpperCase() + m[1].slice(1).toLowerCase()
  const lower = s.toLowerCase()
  if (lower === 'signed' || lower.startsWith('junk') || lower.startsWith('likely junk')) {
    return 'Signed'
  }
  return null
}

function _normKey(value) {
  const s = String(value ?? '').trim()
  if (!s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none') return ''
  return s
}

/**
 * Build a row predicate for optimistic cascade removal when Luminate discovery
 * statuses exclude tracks/artists (Signed/Junk Track|Artist|Label).
 *
 * @param {string} status
 * @param {{ alsoUpdate?: string[], row?: object }} ctx
 * @param {{ artistCol?: string, imprintCol?: string, songIdCol?: string }} cols
 * @returns {(row: object) => boolean}
 */
function _luminateTierFromOption(option) {
  const s = String(option ?? '').trim()
  if (!s) return null
  if (/label/i.test(s)) return 'label'
  if (/track/i.test(s)) return 'track'
  if (/artist/i.test(s)) return 'artist'
  // Sheet-canonical bare polarity defaults to artist tier
  if (/^(signed|unsigned|junk|likely junk)$/i.test(s)) return 'artist'
  return null
}

/**
 * Build a row predicate for optimistic cascade removal when Luminate discovery
 * statuses exclude tracks/artists (Signed/Junk Track|Artist|Label).
 *
 * @param {string} status
 * @param {{ alsoUpdate?: string[], row?: object }} ctx
 * @param {{ artistCol?: string, imprintCol?: string, songIdCol?: string|null }} cols
 * @returns {(row: object) => boolean}
 */
export function luminateRowsToRemovePredicate(
  status,
  { alsoUpdate = [], row } = {},
  { artistCol = 'Primary Artist', imprintCol = 'Imprint', songIdCol = 'Luminate Song ID' } = {},
) {
  const tiers = [status, ...(alsoUpdate || [])].filter(Boolean)
  const excluded = tiers.filter((s) => {
    const raw = String(s)
    if (LUMINATE_EXCLUDED_STATUSES.has(raw)) return true
    return luminateStatusPolarity(raw) === 'Signed'
  })
  if (!excluded.length || !row) return () => false

  let removeSong = false
  let removeArtist = false
  let removeLabel = false
  for (const raw of excluded) {
    const tier = _luminateTierFromOption(raw)
    if (tier === 'label') removeLabel = true
    else if (tier === 'track') removeSong = true
    else removeArtist = true
  }

  const songId = songIdCol ? _normKey(row[songIdCol]) : ''
  const artist = artistCol ? _normKey(row[artistCol]) : ''
  const imprint = imprintCol ? _normKey(row[imprintCol]) : ''

  return (r) => {
    if (removeLabel && imprint && _normKey(r?.[imprintCol]) === imprint) return true
    if (removeArtist && artist && _normKey(r?.[artistCol]) === artist) return true
    if (removeSong && songId && _normKey(r?.[songIdCol]) === songId) return true
    return false
  }
}

/**
 * After a successful Luminate status save, fan out Track / Artist / Label status
 * onto matching rows and recompute effective ``Status`` (track > artist > imprint).
 *
 * Unsigned Artist / Unsigned Label must update every matching discovery row, not
 * only the song/artist row the user clicked.
 */
export function applyLuminateStatusCascade(
  rows,
  {
    sourceRow,
    nextStatus,
    alsoUpdate = [],
    artistCol = 'Primary Artist',
    imprintCol = 'Imprint',
    songIdCol = 'Luminate Song ID',
    statusCol = 'Status',
  } = {},
) {
  if (!Array.isArray(rows) || !sourceRow) return rows

  const options = [nextStatus, ...(alsoUpdate || [])].filter(Boolean)
  if (!options.length) return rows

  const songId = songIdCol ? _normKey(sourceRow[songIdCol]) : ''
  const artist = artistCol ? _normKey(sourceRow[artistCol]) : ''
  const imprint = imprintCol ? _normKey(sourceRow[imprintCol]) : ''

  const tierPatches = []
  for (const opt of options) {
    const tier = _luminateTierFromOption(opt)
    if (!tier) continue
    tierPatches.push({ tier, stored: luminateStoredStatus(opt) || String(opt).trim() })
  }
  if (!tierPatches.length) return rows

  return rows.map((r) => {
    let touched = false
    const next = { ...r }
    for (const { tier, stored } of tierPatches) {
      if (tier === 'track') {
        if (!songId || _normKey(r?.[songIdCol]) !== songId) continue
        next['Track Status'] = stored
        touched = true
      } else if (tier === 'artist') {
        if (!artist || _normKey(r?.[artistCol]) !== artist) continue
        next['Primary Artist Status'] = stored
        touched = true
      } else if (tier === 'label') {
        if (!imprint || _normKey(r?.[imprintCol]) !== imprint) continue
        next['Imprint Status'] = stored
        touched = true
      }
    }
    if (!touched) return r
    const track = String(next['Track Status'] ?? '').trim()
    const art = String(next['Primary Artist Status'] ?? '').trim()
    const lab = String(next['Imprint Status'] ?? '').trim()
    next[statusCol] = track || art || lab || null
    return next
  })
}

export function normalizeStatusOptions(options) {
  if (!options?.length) return []
  return options.map((opt) =>
    typeof opt === 'string'
      ? { value: opt, label: opt, disabled: false }
      : {
          value: opt.value,
          label: opt.label ?? opt.value,
          disabled: Boolean(opt.disabled),
        },
  )
}

/** Display label for a stored status value (falls back to the raw value). */
export function statusDisplayLabel(value, options) {
  if (value == null || value === '') return ''
  const normalized = normalizeStatusOptions(options)
  const match = normalized.find((opt) => opt.value === String(value))
  return match?.label ?? String(value)
}

/** Strip the Snoop dash `snoop:` / `artist:` namespace from a merged status value. */
function bareStatusValue(value) {
  return String(value ?? '')
    .trim()
    .replace(/^(snoop|artist):/, '')
}

/** Compare Status for unsigned tables (Priority < Archive < CM < rest). */
export function compareUnsignedStatus(a, b) {
  const ra = UNSIGNED_STATUS_SORT_RANK[bareStatusValue(a)]
  const rb = UNSIGNED_STATUS_SORT_RANK[bareStatusValue(b)]
  const left = ra === undefined ? 99 : ra
  const right = rb === undefined ? 99 : rb
  if (left !== right) return left - right
  return bareStatusValue(a).localeCompare(bareStatusValue(b), undefined, {
    sensitivity: 'base',
  })
}

// ---------------------------------------------------------------------------
// PGP (publishing / writer-producer scouting)
// Mirrors app/services/pgp.py, which mirrors zRadioPubDb.py. Keep in sync.
// ---------------------------------------------------------------------------

export const PGP_STATUS_OPTIONS = [
  { value: 'Destroy', label: 'Destroy' },
  { value: 'Archive', label: 'Archive' },
  { value: 'Watching', label: 'Watching' },
  { value: 'Priority', label: 'Priority' },
  { value: 'Proposal', label: 'Proposal' },
  { value: 'Longform', label: 'Longform' },
  { value: 'Roster', label: 'Roster' },
]

/** Charts Status action — flags genius_credit_history.is_false_credit, not a pub_mappings status. */
export const PGP_FALSE_CREDIT_STATUS = 'False Credit'

export const PGP_CHARTS_STATUS_OPTIONS = [
  ...PGP_STATUS_OPTIONS,
  { value: PGP_FALSE_CREDIT_STATUS, label: 'False Credit' },
]

/** Hidden from the default Charts view (Destroy only, mirroring the backend). */
export const PGP_CHARTS_HIDDEN_STATUSES = new Set(['Destroy'])

/** Agenda pipeline order (mirrors PGP_AGENDA_STATUSES in the backend). */
export const PGP_AGENDA_STATUSES = ['Longform', 'Proposal', 'Priority', 'Watching']

export const PGP_AGENDA_STATUS_SORT_RANK = {
  Longform: 0,
  Proposal: 1,
  Priority: 2,
  Watching: 3,
}

export const PGP_LEAD_AR_OPTIONS = [
  { value: 'spiller', label: 'spiller' },
  { value: 'b.jacques', label: 'b.jacques' },
  { value: 'alfonso', label: 'alfonso' },
  { value: 'sharif', label: 'sharif' },
  { value: 'zak', label: 'zak' },
  { value: 'joie', label: 'joie' },
  { value: 'bones', label: 'bones' },
  { value: 'l2tk', label: 'L2TK' },
  { value: 'fresh produce', label: 'fresh produce' },
]

/**
 * Full PGP status order for the Mapping tab: the agenda pipeline first, then
 * Archive, then everything else. Roster/Destroy sort after Archive so the
 * active deals stay at the top of the table.
 */
export const PGP_MAPPING_STATUS_SORT_RANK = {
  Longform: 0,
  Proposal: 1,
  Priority: 2,
  Watching: 3,
  Archive: 4,
  Roster: 5,
  Destroy: 6,
}

/** Sort Status by the Mapping tab's stage order; unknown/blank last. */
export function comparePgpMappingStatus(a, b) {
  const ra = PGP_MAPPING_STATUS_SORT_RANK[bareStatusValue(a)]
  const rb = PGP_MAPPING_STATUS_SORT_RANK[bareStatusValue(b)]
  const left = ra === undefined ? 99 : ra
  const right = rb === undefined ? 99 : rb
  if (left !== right) return left - right
  return bareStatusValue(a).localeCompare(bareStatusValue(b), undefined, {
    sensitivity: 'base',
  })
}

export function isPgpChartsExcludedStatus(status) {
  const s = String(status ?? '').trim()
  if (!s) return false
  return PGP_CHARTS_HIDDEN_STATUSES.has(s)
}

/** Sort Status by pipeline stage (Longform first), not alphabetically. */
export function comparePgpAgendaStatus(a, b) {
  const ra = PGP_AGENDA_STATUS_SORT_RANK[bareStatusValue(a)]
  const rb = PGP_AGENDA_STATUS_SORT_RANK[bareStatusValue(b)]
  const left = ra === undefined ? 99 : ra
  const right = rb === undefined ? 99 : rb
  if (left !== right) return left - right
  return bareStatusValue(a).localeCompare(bareStatusValue(b), undefined, {
    sensitivity: 'base',
  })
}
